/**
 * usePlayerSockets — Hub centralizado de canais Supabase Realtime do Player.
 *
 * Arquitetura de canais (v3):
 *
 *   cmd_tela_{telaId}      → CANAL PRIVADO de comandos e dados:
 *                              remote_cmd   controle remoto (play/pause/next/…)
 *                              admin_cmd    Admin Master (restart/revoke/force_update)
 *                              cmd          atualização de configurações
 *                              db_change    INSERT/UPDATE/DELETE de midias e UPDATE de telas,
 *                                           publicado por trigger (realtime.send) — o anon não
 *                                           tem SELECT nessas tabelas, então postgres_changes
 *                                           nunca chegava na TV
 *
 *   canal_tela_{telaId}    → CANAL PÚBLICO de status: jukebox_cmd
 *   platform_cmd           → force_update global privado (canal compartilhado entre todas as telas)
 *   fila_tela_{telaId}     → broadcast privado `fila` (condicional, só com jukebox)
 *
 * Canais eliminados (v1 → v2):
 *   admin-cmd-{telaId}     fundido em canal_tela_ como event 'admin_cmd'
 *   jukebox broadcast      fundido em canal_tela_ como event 'jukebox_cmd'
 *
 * Proteções de rede implementadas:
 *   • State Storm guard   — debounce 2 s em solicitar_estado (múltiplos garçons simultâneos)
 *   • Passive sync        — emite musica_atual a cada 15 s (elimina polling de celulares)
 *   • canalTelaReadyRef       — bloqueia send() antes de SUBSCRIBED e após CLOSED/erro
 *   • cbRef                   — stale closure prevention para TVs rodando 24/7
 *   • relógio do canal        — watchdog só trata o socket como vivo após ACK do servidor
 *   • Watchdog WS             — recria todos os canais após 3 min sem confirmação
 *   • pendingNetworkRestored  — gate preciso para onNetworkRestored (só watchdog, não auto-reconnect)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { TOPICO_PLATAFORMA, topicoComandosTela, topicoFilaTela, topicoStatusTela } from '@/shared/lib/canaisTela';
import type { Midia, Tela, FilaJukebox, MusicaAtualBroadcast, PdvEngajamentoBroadcast } from '@/shared/types';
import { acharCanal, relogioAvanca } from '@/features/player/domain/usecases/canalRealtime';
import { midiaValida, momentoEspecialVideoValido, registroDaTela, telaDoPayload } from '@/features/player/domain/usecases/validarPayloadRealtime';

const WS_RECONNECT_TIMEOUT_MS = 3 * 60 * 1_000; // 3 min sem heartbeat → recria canais
const WS_CHECK_INTERVAL_MS    = 30_000;           // frequência do watchdog
const DEBOUNCE_ESTADO_MS      = 2_000;            // janela State Storm guard
const PASSIVE_SYNC_MS         = 15_000;           // intervalo de emissão passiva

export interface PlayerSocketCallbacks {
  onAdminRevoke(): void;
  onContaSuspensa(): void;
  onRemoteCmd(cmd: string, payload: Record<string, unknown>): void;
  onUpdateSettings(payload: unknown): void;
  onMidiaInsert(midia: Midia): void;
  onMidiaUpdate(midia: Midia): void;
  onMidiaDelete(midia: Partial<Midia>): void;
  onTelaUpdate(tela: Tela): void;
  onNovoJukeboxPedido(item: FilaJukebox): void;
  onFilaJukeboxUpdate(item: FilaJukebox): void;
  onFilaJukeboxDelete(id: string): void;
  onSolicitarMusicaAtual(): void;
  onMomentoEspecial(nome: string, videoUrl: string | undefined): void;
  onSetVolume(volume: number): void;
  /** Disparado quando o handshake WebSocket é concluído após o WS Watchdog recriar os canais. */
  onNetworkRestored(): void;
  /** Todo SUBSCRIBED do canal core depois do primeiro (auto-reconnect do
   *  Supabase OU watchdog): só catch-up de dados, sem mexer na mídia. */
  onCanalRessincronizado(): void;
  /** Evento `pdv_engajamento` emitido pela Edge Function do PDV. */
  onPdvEngajamento(payload: PdvEngajamentoBroadcast): void;
  /** `campanhas_mudaram` em `platform_cmd`: rebuscar as campanhas da plataforma desta tela. */
  onCampanhasMudaram(): void;
  hasJukebox: boolean;
}

export interface UsePlayerSocketsReturn {
  sendToTela(event: string, payload: Record<string, unknown>): void;
}

async function limparCachesERecarregar(): Promise<void> {
  if ('caches' in window) {
    const chaves = await caches.keys();
    await Promise.all(chaves.map(k => caches.delete(k)));
  }
  if ('serviceWorker' in navigator) {
    const registros = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registros.map(r => r.unregister()));
  }
  window.location.reload();
}

function aplicarEventoFila(payload: unknown, cb: PlayerSocketCallbacks, telaId: string): void {
  if (!payload || typeof payload !== 'object') return;
  const corpo = payload as { operation?: unknown; new?: unknown; old?: unknown };
  if (corpo.operation === 'INSERT' || corpo.operation === 'UPDATE') {
    const item = corpo.new as FilaJukebox | undefined;
    if (!item || typeof item.id !== 'string' || item.id.length === 0) return;
    if (!registroDaTela(item, telaId)) return;
    if (corpo.operation === 'INSERT') {
      if (item.status === 'pendente') cb.onNovoJukeboxPedido(item);
      return;
    }
    cb.onFilaJukeboxUpdate(item);
    return;
  }
  if (corpo.operation === 'DELETE') {
    const id = (corpo.old as { id?: unknown } | null | undefined)?.id;
    if (typeof id !== 'string' || id.length === 0) return;
    cb.onFilaJukeboxDelete(id);
  }
}

export function usePlayerSockets(
  telaId: string | undefined,
  callbacks: PlayerSocketCallbacks,
): UsePlayerSocketsReturn {
  // cbRef: isola o ciclo de vida dos canais das re-renderizações do React.
  // Sem isso, closures dentro dos handlers capturariam valores obsoletos de
  // callbacks depois de uma re-renderização (stale closure em TVs 24/7).
  const cbRef = useRef<PlayerSocketCallbacks>(callbacks);
  useEffect(() => { cbRef.current = callbacks; });

  const canalTelaRef      = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const canalTelaReadyRef = useRef(false);
  const [reconnectKey, setReconnectKey] = useState(0);

  // Sinaliza que o próximo SUBSCRIBED é resultado do nosso watchdog — não de um
  // auto-reconnect do Supabase. Garante que onNetworkRestored não dispare
  // espuriamente em cada auto-reconexão após o primeiro reconnectKey > 0.
  const pendingNetworkRestoredRef = useRef(false);
  const jaAssinouRef              = useRef(false);
  const jaAssinouCmdRef           = useRef(false);
  const cmdCaiuEmRef              = useRef<number | null>(null);

  // lastConnectedAtRef: timestamp da última confirmação de conexão WebSocket.
  // Alimentado pelo ACK do passive sync e pelo subscribe SUBSCRIBED.
  const lastConnectedAtRef = useRef<number>(Date.now());

  // State Storm guard: guarda timestamp do último broadcast de estado emitido.
  // Requisições dentro da janela de 2 s são descartadas silenciosamente.
  const ultimoEnvioEstadoRef = useRef<number>(0);

  // sendToTela: fire-and-forget estável (deps vazias) — usa ref do canal.
  const sendToTela = useCallback((event: string, payload: Record<string, unknown>) => {
    if (!canalTelaReadyRef.current || !canalTelaRef.current) return;
    canalTelaRef.current
      .send({ type: 'broadcast', event, payload })
      .then(resposta => {
        if (relogioAvanca(resposta)) lastConnectedAtRef.current = Date.now();
      })
      .catch(() => {});
  }, []);

  // ── Inscrição de todos os canais ──────────────────────────────────────────
  useEffect(() => {
    if (!telaId) return;
    let cancelado = false;
    let canalCmd: ReturnType<typeof supabase.channel> | null = null;
    let canalTela: ReturnType<typeof supabase.channel> | null = null;
    let canalPlatform: ReturnType<typeof supabase.channel> | null = null;
    let canalJukebox: ReturnType<typeof supabase.channel> | null = null;

    const hasJukebox   = callbacks.hasJukebox;
    const onSubscribed = () => { lastConnectedAtRef.current = Date.now(); };
    const onForceUpdate = () => { limparCachesERecarregar().catch(() => {}); };

    const nomeCmd = topicoComandosTela(telaId);
    const nomeStatus = topicoStatusTela(telaId);
    const nomeFila = topicoFilaTela(telaId);
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    void (async () => {
      for (const nome of hasJukebox ? [nomeCmd, nomeStatus, nomeFila] : [nomeCmd, nomeStatus]) {
        const ocupado = acharCanal(supabase.getChannels(), nome);
        if (ocupado) await supabase.removeChannel(ocupado);
      }
      if (cancelado) return;

      // ── CANAL PRIVADO: comandos e dados do servidor ──────────────────────────
      cmdCaiuEmRef.current = null;
      canalCmd = supabase
        .channel(nomeCmd, { config: { private: true, broadcast: { ack: true } } })

      // remote_cmd: controle remoto + trigger de momentos especiais
      .on('broadcast', { event: 'remote_cmd' }, ({ payload }) => {
        const { cmd, ...rest } = payload as { cmd: string; [k: string]: unknown };

        if (cmd === 'trigger_birthday') {
          const { nome, video_url } = rest as { nome: string; video_url?: string };
          cbRef.current.onMomentoEspecial(nome, momentoEspecialVideoValido(video_url, supabaseUrl));
          return;
        }

        if (cmd === 'set_volume') {
          const { volume } = rest as { volume: number };
          cbRef.current.onSetVolume(volume);
          return;
        }

        // State Storm guard: descarta requisições de solicitar_estado
        // recebidas dentro da janela de 2 s (ex: vários garçons abrindo o app)
        if (cmd === 'solicitar_estado') {
          const agora = Date.now();
          if (agora - ultimoEnvioEstadoRef.current < DEBOUNCE_ESTADO_MS) return;
          ultimoEnvioEstadoRef.current = agora;
        }

        cbRef.current.onRemoteCmd(cmd, rest as Record<string, unknown>);
      })

      // admin_cmd: Admin Master — substitui o canal admin-cmd-{telaId}
      .on('broadcast', { event: 'admin_cmd' }, ({ payload }) => {
        const { cmd } = payload as { cmd: string };
        if (cmd === 'restart')        { window.location.reload();          return; }
        if (cmd === 'revoke')         { cbRef.current.onAdminRevoke();      return; }
        if (cmd === 'conta_suspensa') { cbRef.current.onContaSuspensa();    return; }
        if (cmd === 'force_update')   { onForceUpdate();                    return; }
      })

      // cmd: broadcast de atualização de configurações (controle remoto / painel)
      .on('broadcast', { event: 'cmd' }, ({ payload }) => {
        cbRef.current.onUpdateSettings(payload);
      })

      // db_change: trigger de midias/telas (migration_zzz_player_db_change_broadcast.sql)
      .on('broadcast', { event: 'db_change' }, ({ payload }) => {
        const p = payload as { tabela: string; tipo: string; new?: unknown; old?: unknown };
        if (p.tabela === 'midias') {
          if (p.tipo === 'INSERT' || p.tipo === 'UPDATE') {
            if (!midiaValida(p.new, supabaseUrl) || !registroDaTela(p.new, telaId)) return;
            if (p.tipo === 'INSERT') cbRef.current.onMidiaInsert(p.new);
            else cbRef.current.onMidiaUpdate(p.new);
          }
          else if (p.tipo === 'DELETE') {
            if (typeof (p.old as { id?: unknown } | null)?.id === 'string') {
              cbRef.current.onMidiaDelete(p.old as Partial<Midia>);
            }
          }
        } else if (p.tabela === 'telas' && p.tipo === 'UPDATE' && telaDoPayload(p.new, telaId)) {
          cbRef.current.onTelaUpdate(p.new);
        }
      })

      .on('broadcast', { event: 'pdv_engajamento' }, ({ payload }) => {
        const p = payload as PdvEngajamentoBroadcast;
        if (p?.tipo === 'atualizar' || p?.tipo === 'destaque' || p?.tipo === 'hype_marco') {
          cbRef.current.onPdvEngajamento(p);
        }
      })

      .subscribe(status => {
        if (status === 'SUBSCRIBED') {
          onSubscribed();
          cmdCaiuEmRef.current = null;
          if (jaAssinouCmdRef.current) cbRef.current.onCanalRessincronizado();
          jaAssinouCmdRef.current = true;
          // Só chama onNetworkRestored quando o watchdog explicitamente o agendou.
          // Evita disparos espúrios nos auto-reconnects do Supabase após queda curta.
        } else if (
          status === 'CLOSED' ||
          status === 'TIMED_OUT' ||
          status === 'CHANNEL_ERROR'
        ) {
          if (cmdCaiuEmRef.current === null) cmdCaiuEmRef.current = Date.now();
        }
      });

      canalTela = supabase
        .channel(nomeStatus, { config: { broadcast: { ack: true } } })
        .on('broadcast', { event: 'jukebox_cmd' }, ({ payload }) => {
          const { cmd } = payload as { cmd: string };
          if (cmd === 'solicitar_musica_atual') cbRef.current.onSolicitarMusicaAtual();
        })
        .subscribe(status => {
          if (status === 'SUBSCRIBED') {
            onSubscribed();
            canalTelaReadyRef.current = true;
            if (jaAssinouRef.current) cbRef.current.onCanalRessincronizado();
            jaAssinouRef.current = true;
            if (pendingNetworkRestoredRef.current) {
              pendingNetworkRestoredRef.current = false;
              cbRef.current.onNetworkRestored();
            }
          } else if (
            status === 'CLOSED' ||
            status === 'TIMED_OUT' ||
            status === 'CHANNEL_ERROR'
          ) {
            canalTelaReadyRef.current = false;
          }
        });
      canalTelaRef.current = canalTela;

      // ── Canal global privado de plataforma (compartilhado, não por telaId) ───
      canalPlatform = supabase
        .channel(TOPICO_PLATAFORMA, { config: { private: true } })
        .on('broadcast', { event: 'force_update' }, onForceUpdate)
        .on('broadcast', { event: 'campanhas_mudaram' }, () => cbRef.current.onCampanhasMudaram())
        .subscribe();

      // Fila do anon chega só pelo broadcast privado. Comandos de tenant/admin
      // continuam em cmd_tela_ / canal_tela_ / platform_cmd.
      if (hasJukebox) {
        canalJukebox = supabase
          .channel(nomeFila, { config: { private: true } })
          .on('broadcast', { event: 'fila' }, ({ payload }) => {
            aplicarEventoFila(payload, cbRef.current, telaId);
          })
          .subscribe(status => { if (status === 'SUBSCRIBED') onSubscribed(); });
      }
    })();

    return () => {
      cancelado = true;
      canalTelaReadyRef.current = false;
      canalTelaRef.current = null;
      if (canalCmd) void supabase.removeChannel(canalCmd);
      if (canalTela) void supabase.removeChannel(canalTela);
      if (canalPlatform) void supabase.removeChannel(canalPlatform);
      if (canalJukebox) void supabase.removeChannel(canalJukebox);
    };
  }, [telaId, callbacks.hasJukebox, reconnectKey]);

  // ── Passive sync loop ─────────────────────────────────────────────────────
  // Emite musica_atual a cada 15 s independentemente de requisições.
  // Garante que celulares que acabam de abrir o Jukebox recebem estado da TV
  // sem precisar enviar solicitar_musica_atual.
  useEffect(() => {
    if (!telaId) return;
    const interval = setInterval(() => {
      if (!canalTelaReadyRef.current || !canalTelaRef.current) return;
      sendToTela('player_heartbeat', {});
      cbRef.current.onSolicitarMusicaAtual();
    }, PASSIVE_SYNC_MS);
    return () => clearInterval(interval);
  }, [telaId, sendToTela]);

  // ── Watchdog de reconexão WebSocket ──────────────────────────────────────
  // Verifica a cada 30 s se lastConnectedAtRef foi atualizado nos últimos 3 min.
  // O relógio anda no SUBSCRIBED e nos ACKs do passive sync.
  useEffect(() => {
    if (!telaId) return;
    const interval = setInterval(() => {
      const agora = Date.now();
      const offlineMs = agora - lastConnectedAtRef.current;
      const cmdIndisponivelPorMuitoTempo = cmdCaiuEmRef.current !== null
        && agora - cmdCaiuEmRef.current >= WS_RECONNECT_TIMEOUT_MS;
      if (offlineMs >= WS_RECONNECT_TIMEOUT_MS || cmdIndisponivelPorMuitoTempo) {
        lastConnectedAtRef.current      = agora; // reset antes do bump (evita loop)
        pendingNetworkRestoredRef.current = true;     // sinaliza: próximo SUBSCRIBED = rede restaurada
        cmdCaiuEmRef.current = null;
        setReconnectKey(k => k + 1);
      }
    }, WS_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [telaId]);

  return { sendToTela };
}

export type { MusicaAtualBroadcast };
